import {
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  Stack,
  Typography,
} from "@mui/material";
import CloseRounded from "@mui/icons-material/CloseRounded";
import { useState } from "react";
import Button from "../../../../../../../components/common/Button";
import { ProposalService, Service, useServices } from "./hooks/UseServices";

// ─── Props ────────────────────────────────────────────────────────────────────

interface AddServiceDialogProps {
  open: boolean;
  onClose: () => void;
  onAdd: (ps: ProposalService) => void;
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function AddServiceDialog({
  open,
  onClose,
  onAdd,
}: AddServiceDialogProps) {
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [selectedService, setSelectedService] = useState<Service | null>(null);

  const { services, loading, nextPageUrl, prevPageUrl, fetchServices } =
    useServices(selectedCategory);

  const uniqueCategories = [
    { id: "all", label: "All" },
    ...Array.from(
      new Map(
        services.map((s) => [
          s.service_category,
          {
            id: s.service_category,
            label: s.service_category_display || s.service_category,
          },
        ]),
      ).values(),
    ),
  ];

  const handleAdd = () => {
    if (!selectedService) return;
    onAdd({ service: selectedService, quantity: 1 });
    setSelectedService(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography
            sx={{ fontWeight: 700, fontSize: "16px", color: "#1e293b" }}
          >
            Add Service
          </Typography>
          <IconButton onClick={onClose} size="small">
            <CloseRounded sx={{ fontSize: 18 }} />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent sx={{ pb: 3 }}>
        <Box sx={{ display: "flex", gap: 3, minHeight: 400 }}>
          {/* Categories sidebar */}
          <Box
            sx={{
              width: 160,
              flexShrink: 0,
              borderRight: "1px solid #e2e8f0",
              pr: 2,
            }}
          >
            <Typography
              sx={{
                fontSize: "11px",
                fontWeight: 700,
                color: "#94a3b8",
                mb: 1.5,
                textTransform: "uppercase",
              }}
            >
              Categories
            </Typography>
            <List disablePadding>
              {uniqueCategories.map((cat) => (
                <ListItem key={cat.id} disablePadding sx={{ mb: 0.5 }}>
                  <ListItemButton
                    selected={selectedCategory === cat.id}
                    onClick={() => setSelectedCategory(cat.id)}
                    sx={{
                      borderRadius: "8px",
                      py: 0.8,
                      px: 1.5,
                      "&.Mui-selected": {
                        bgcolor: "#fff8f2",
                        color: "#724e41",
                        fontWeight: 600,
                      },
                    }}
                  >
                    <Typography sx={{ fontSize: "12px" }}>
                      {cat.label}
                    </Typography>
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          </Box>

          {/* Services list */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            {loading ? (
              <Box sx={{ display: "flex", justifyContent: "center", pt: 4 }}>
                <CircularProgress size={24} sx={{ color: "#724e41" }} />
              </Box>
            ) : selectedService ? (
              // Service detail view
              <Box>
                <Button
                  variant="text"
                  onClick={() => setSelectedService(null)}
                  sx={{ mb: 2, color: "#64748b", fontSize: "12px", p: 0 }}
                >
                  ← Back
                </Button>
                <Typography
                  sx={{
                    fontWeight: 700,
                    fontSize: "15px",
                    color: "#1e293b",
                    mb: 0.5,
                  }}
                >
                  {selectedService.name}
                </Typography>
                <Typography sx={{ fontSize: "12px", color: "#64748b", mb: 2 }}>
                  {selectedService.description || "No description available."}
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    bgcolor: "#f8fafc",
                    borderRadius: "10px",
                    px: 2,
                    py: 1.5,
                    mb: 2,
                  }}
                >
                  <Typography sx={{ fontSize: "13px", color: "#475569" }}>
                    Price
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: "14px",
                      fontWeight: 700,
                      color: "#1e293b",
                    }}
                  >
                    ${selectedService.price.toLocaleString()}
                  </Typography>
                </Box>
                <Button
                  variant="contained"
                  fullWidth
                  onClick={handleAdd}
                  sx={{
                    borderRadius: "10px",
                    py: 1.2,
                    textTransform: "none",
                    fontWeight: 700,
                  }}
                >
                  Add to Proposal
                </Button>
              </Box>
            ) : (
              // Services list
              <>
                <Stack spacing={1.5}>
                  {services.map((svc) => (
                    <Box
                      key={svc.id}
                      onClick={() => setSelectedService(svc)}
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        bgcolor: "#f8fafc",
                        borderRadius: "10px",
                        px: 2,
                        py: 1.5,
                        cursor: "pointer",
                        border: "1px solid transparent",
                        transition: "all 0.15s",
                        "&:hover": {
                          borderColor: "#c59a70",
                          bgcolor: "#fff8f2",
                        },
                      }}
                    >
                      <Box>
                        <Typography
                          sx={{
                            fontSize: "13px",
                            fontWeight: 600,
                            color: "#1e293b",
                          }}
                        >
                          {svc.name}
                        </Typography>
                        <Typography sx={{ fontSize: "11px", color: "#94a3b8" }}>
                          {svc.service_category_display || svc.service_category}
                        </Typography>
                      </Box>
                      <Typography
                        sx={{
                          fontSize: "13px",
                          fontWeight: 700,
                          color: "#475569",
                        }}
                      >
                        ${svc.price.toLocaleString()}
                      </Typography>
                    </Box>
                  ))}
                  {services.length === 0 && (
                    <Typography
                      sx={{
                        fontSize: "13px",
                        color: "#94a3b8",
                        textAlign: "center",
                        py: 3,
                      }}
                    >
                      No services in this category.
                    </Typography>
                  )}
                </Stack>

                {/* Pagination */}
                {(prevPageUrl || nextPageUrl) && (
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    sx={{ mt: 2 }}
                  >
                    <Button
                      variant="outlined"
                      disabled={!prevPageUrl}
                      onClick={() => fetchServices(prevPageUrl!)}
                      sx={{ fontSize: "12px", borderRadius: "8px" }}
                    >
                      Previous
                    </Button>
                    <Button
                      variant="outlined"
                      disabled={!nextPageUrl}
                      onClick={() => fetchServices(nextPageUrl!)}
                      sx={{ fontSize: "12px", borderRadius: "8px" }}
                    >
                      Next
                    </Button>
                  </Stack>
                )}
              </>
            )}
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
}
